import { useEffect, useState } from "react";
import LogoFallback from "/assets/img/bubbanlogo.jpg";
import axios from "axios";
import { motion } from 'framer-motion';

export default function AcCalendar({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Academic Calendar | Bandarban University",
}) {
	const [calendar, setCalendar] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	
	const calendarPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,				
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/academic-calendar", payload);
			
			setCalendar(response.data);
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		calendarPost();
	}, []);
	
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-700 to-teal-500 text-white py-20 overflow-hidden">
        <div className="container mx-auto px-6 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold animate-fadeIn">
            Academic Calendar
          </h1>
          <p className="mt-4 text-lg opacity-90 animate-fadeIn delay-200">
            Stay updated with important academic schedules and events
          </p>
        </div>

        {/* Floating animated shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute w-24 h-24 bg-white/20 rounded-full animate-bounce top-10 left-10"></div>
          <div className="absolute w-32 h-32 bg-white/10 rounded-full animate-pulse bottom-10 right-10"></div>
        </div>
      </section>

      {/* Calendar Section */}
      {/* Timeline Section */}
      <section className="relative max-w-5xl mx-auto px-6 py-16">
        {loading ? (
          <div className="text-center text-[#014864] font-medium">
            Loading academic calendar...
          </div>
        ) : (
          <div className="relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-1/2 top-0 h-full w-1 bg-yellow-400 transform -translate-x-1/2"></div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="space-y-16"
            >
              {calendar.map((item, index) => (
                <motion.div
                  key={item.SL}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  className={`flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:justify-start' : 'md:justify-end'
                  } relative`}
                >
                  {/* Connector Dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 bg-[#014864] w-6 h-6 rounded-full border-4 border-amber-100 z-10"></div>

                  {/* Content Card */}
                  <div
                    className={`bg-white rounded-2xl shadow-lg border border-yellow-200 p-6 w-full md:w-[45%] ${
                      index % 2 === 0 ? 'md:mr-auto md:text-right' : 'md:ml-auto md:text-left'
                    }`}
                  >
                    <h3 className="text-2xl font-bold text-[#014864] mb-1">
                      {item.name}
                    </h3>
                    <p className="text-[#015A7A] font-medium mb-1">
                      Semester: {item.semester}
                    </p>
                    <p className="text-[#015A7A]">
                      <strong>Start:</strong> {item.start_date}
                    </p>
                    <p className="text-[#015A7A]">
                      <strong>End:</strong> {item.end_date}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}
      </section>

      {/* Inline animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 1s ease-out forwards;
        }
        .delay-200 {
          animation-delay: 0.2s;
        }
      `}</style>
    </div>
  );
}
